import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { SessionState } from 'sip.js';
import * as actions from '~/store/actions';
import {
  log,
  sdpModifierCodec,
  enableSenderTracks,
  electron,
} from '~/helpers/';

export default function useCallHold(value = false) {
  const dispatch = useDispatch();
  const store = useStore();

  const setCallHold = useCallback(
    (session, held = value) =>
      new Promise(resolve => {
        if (session?.state !== SessionState.Established) {
          resolve(false);
          return;
        }

        const pc = session.sessionDescriptionHandler?.peerConnection;

        if (!pc) {
          resolve(false);
          return;
        }

        session.sessionDescriptionHandlerOptionsReInvite = {
          ...session.sessionDescriptionHandlerOptionsReInvite,
          hold: held,
        };
        session.sessionDescriptionHandlerModifiersReInvite = [sdpModifierCodec];

        session
          .invite({
            requestDelegate: {
              onAccept: () => {
                const muted = store
                  .getState()
                  ?.callCurrent?.data?.find(e => e.id === session.id)?.mute;

                enableSenderTracks(pc, !held && !muted);

                if (!held) {
                  const remoteStream = new MediaStream();

                  pc.getReceivers().forEach(({ track }) => {
                    if (track) {
                      remoteStream.addTrack(track);
                    }
                  });
                  dispatch(actions.remoteVideo(remoteStream));
                }

                dispatch(
                  actions.updateCall(held ? 'hold' : 'unhold', session.id)
                );

                // Behind the `desktop_yealink_call_control` feature flag: tells Electron to
                // sync the Yealink device's hold state with the call, only once the re-INVITE
                // that actually holds/resumes the call has been accepted.
                if (
                  store.getState().accountInfo.featureFlags
                    .isYealinkCallControlEnabled
                ) {
                  if (held) {
                    electron.setCallOnHold(session.id);
                  } else {
                    electron.setCallResume(session.id);
                  }
                }

                resolve(true);
              },
              onReject: response => {
                log.error(
                  `Failed to set hold to ${held} for session ${session.id}. Reason: ${response.message?.statusCode} ${response.message?.reasonPhrase}`
                );
                resolve(false);
              },
            },
          })
          .catch(err => {
            log.error(
              `Failed to send the re-INVITE to set hold to ${held} for session ${session.id}.`,
              err
            );
            resolve(false);
          });
      }),
    [dispatch, store, value]
  );

  return setCallHold;
}
